// WHAT: Meal Card Component (Molecule)
// WHY:  Displays meal information with calorie tracking and progress bar
// DOMAIN: Health → Yazio
// DEPENDENCIES: CalorieBadge atom, shared utils, MealCardProps type
// RISKS:
//   - NOTE: Progress thresholds are hardcoded (80% = near complete, 100% = complete)
//   - NOTE: Card border color changes based on completion status
//   - NOTE: Progress bar width is clamped to 100% max

import type { MealCardProperties } from "../../types";

import { cn } from "@/shared/utils";

import { CalorieBadge } from "../atoms/CalorieBadge";

/**
 * WHAT:
 *   Card component displaying meal information with calorie tracking and progress.
 *
 * WHY:
 *   Provides visual meal tracking with completion status and calorie information.
 *
 * PROPS:
 *   - title: string — Meal name (e.g., "Breakfast", "Lunch")
 *   - meal: object — Meal data with calories and goal
 *   - icon: ReactNode — Icon to display next to meal title
 *   - className?: string — Additional CSS classes
 *
 * BEHAVIOR:
 *   - Calculates meal progress percentage
 *   - Color codes card border: green if complete (100%), yellow if near complete (80%+), gray otherwise
 *   - Displays CalorieBadge with meal calories and goal
 *   - Shows progress bar with color coding matching card border
 *   - Progress bar width clamped to 100% max
 *   - Applies hover effect on incomplete meals
 *
 * RISKS:
 *   - NOTE: Progress thresholds are hardcoded (80% and 100%)
 *   - NOTE: Progress bar doesn't show values > 100% visually
 *   - NOTE: Card styling changes based on completion (may cause layout shift)
 *
 * @param root0
 *
 * @param root0.title
 *
 * @param root0.meal
 *
 * @param root0.icon
 *
 * @param root0.className
 *
 * @returns {JSX.Element} Meal card component
 *
 */
export function MealCard({ className, icon, meal, title }: Readonly<MealCardProperties>) {
  const mealCalories = meal.calories;
  const mealGoal = meal.goal;
  const percentage = mealGoal > 0 ? (mealCalories / mealGoal) * 100 : 0;
  const isComplete = percentage >= 100;
  const isNearComplete = percentage >= 80;

  const getBorderClassName = (): string => {
    if (isComplete) {
      return "border-green-500/30 bg-green-900/10";
    }
    if (isNearComplete) {
      return "border-yellow-500/30 bg-yellow-900/10";
    }
    return "border-slate-700 hover:border-slate-600";
  };

  const getProgressBarColor = (): string => {
    if (isComplete) {
      return "bg-green-500";
    }
    if (isNearComplete) {
      return "bg-yellow-500";
    }
    return "bg-blue-500";
  };

  return (
    <div
      className={cn(
        "bg-slate-800/50 rounded-lg p-4 border transition-all duration-200",
        getBorderClassName(),
        typeof className === "string" ? className : undefined,
      )}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center space-x-2">
          <div className="text-slate-400">{icon}</div>
          <h3 className="font-medium text-white">{title}</h3>
        </div>
        {}
        <CalorieBadge calories={meal.calories} goal={meal.goal} size="sm" />
      </div>

      <div className="space-y-2">
        <div className="flex justify-between text-sm text-slate-400">
          <span>Progress</span>
          <span>{Math.round(percentage)}%</span>
        </div>
        <div className="w-full bg-slate-700 rounded-full h-2">
          <div
            className={cn("h-2 rounded-full transition-all duration-300", getProgressBarColor())}
            style={{ width: `${String(Math.min(percentage, 100))}%` }}
          />
        </div>
      </div>
    </div>
  );
}
